import { type Academy, isCreverseAcademy } from '@mathflat/domain/Academy'
import {
  type CurriculumCore,
  DEFAULT_HIGH_SCHOOL_CURRICULUM_KEY,
} from '@mathflat/domain/Curriculum'
import { SCHOOL_TYPE_KO_ABBR } from '@mathflat/domain/SchoolSystem'
import type { Student, StudentAppSetting } from '@mathflat/domain/Student'
import { studentApi } from '@mathflat/domain/Student'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { isDevelopMode } from '@mathflat/shared/@common/utils/dev'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import DynamicRepo from '@mathflat/shared/@modules/mobx/repository/dynamicRepo.service'
import ServiceRepo from '@mathflat/shared/@modules/mobx/repository/repo.service'
import { makeAutoObservable, observable, runInAction } from 'mobx'

import { studentAppSettingLoader } from '../api/studentAppSettingLoader'
import { UserPermission } from '../models/(Permission)/UserPermission'
import { envUtils } from '../utils/envUtils'
import { CurriculumService } from './_curriculum.service'
import { ParentRepo } from './parentRepo.service'
import { localStorageService } from './storage.service'

export const serviceRepo = new ServiceRepo()
export const dynamicRepo = new DynamicRepo()

const MATHOLOGY_LOGO_URL = s3URL.mathflatUserUploadsBucket('logo/static/MATHOLOGY_LOGO.svg', {
  isLive: envUtils.isLive,
})

// 저장소가 들고 있는 학생. 로그인 직후에는 id 만 있고, 상세와 앱 설정은 따로 채워진다.
// 도메인 Student 는 상세 응답 그대로이고 appSetting 은 별도 엔드포인트라 앱이 여기서 합친다.
export type LoadedStudent = Partial<Student> & { id: Student['id']; appSetting?: StudentAppSetting }

class CommonRepo {
  private _academy: Academy | null = null
  private _student: LoadedStudent | null = null
  private _curriculumCore: CurriculumService | null = null
  private _permission: UserPermission = new UserPermission({
    userRole: 'STUDENT',
    academyAuthority: 'BASIC',
    subscribeItems: null,
    isSchoolflat: envUtils.isSchool,
    restrictions: [],
  })
  // 초기 _permission 은 restrictions:[] 기본값(빈 블랙리스트=전체 허용)로 동작한다.
  // 서버 응답 전 UI 가 "전체 허용" 상태로 잠깐 보이는 깜빡임을 막기 위해
  private _isPermissionLoaded = false
  // loadInitialData 실패 사유 — 흰 화면 대신 fallback UI 노출 여부 판단용
  private _loadFailureReason: string | null = null
  readonly parent = new ParentRepo()

  constructor() {
    makeAutoObservable<
      this,
      | '_academy'
      | '_student'
      | '_curriculumCore'
      | '_permission'
      | '_isPermissionLoaded'
      | '_loadFailureReason'
    >(this, {
      _academy: observable,
      _student: observable,
      _curriculumCore: observable,
      _permission: observable,
      _isPermissionLoaded: observable,
      _loadFailureReason: observable,
    })
    if (localStorage.getItem('studentId')) {
      const studentId = localStorage.getItem('studentId') as Student['id']
      this._student = { id: studentId }
    }
  }

  get academyLogoUrl() {
    const DEFAULT_MATHFLAT_LOGO_URL =
      'https://mathflat-user-uploads-dev.mathflat.com/logo/Default.png'
    const DEFAULT_SCHOOLFLAT_LOGO_URL =
      'https://mathflat-frontend-asset.s3.ap-northeast-2.amazonaws.com/teacher/assets/schoolflat_logo.png'

    if (commonRepo.isSchoolflat) {
      if (this._academy?.logoUrl === DEFAULT_MATHFLAT_LOGO_URL) {
        return DEFAULT_SCHOOLFLAT_LOGO_URL
      }
    }
    return this._academy?.logoUrl
  }

  /** 크레버스 학부모앱은 학원 로고 대신 매쏠로지 브랜드를 세운다 — 크레버스 학생은 학원 로고 그대로 */
  get gnbLogoUrl() {
    if (this.isCreverse && this.permission.isParent) {
      return MATHOLOGY_LOGO_URL
    }
    return this.academyLogoUrl
  }

  setAcademy = (academy: CommonRepo['_academy']) => {
    this._academy = academy
  }

  setStudent = (student: LoadedStudent) => {
    // 상세·앱 설정이 서로 다른 시점에 오므로 갖고 있던 것 위에 덮는다. 검증은 도메인 adapter 가 이미 했다.
    // 다만 다른 학생(학부모 자녀 전환)으로 바뀌면 이전 학생 필드가 섞이지 않게 병합하지 않는다.
    const base = this._student?.id === student.id ? this._student : undefined
    this._student = { ...base, ...student }
  }

  setCurriculumCore = (curriculumCore: CurriculumCore) => {
    this._curriculumCore = new CurriculumService(curriculumCore)
  }

  setPermission(permission: UserPermission) {
    this._permission = permission
    this._isPermissionLoaded = true
  }

  get loadFailureReason() {
    return this._loadFailureReason
  }

  setLoadFailure = (reason: string) => {
    this._loadFailureReason = reason
  }

  clearLoadFailure = () => {
    this._loadFailureReason = null
  }

  get permission() {
    return this._permission
  }

  get isPermissionLoaded() {
    return this._isPermissionLoaded
  }

  get academy() {
    return this._academy
  }

  get isCreverse() {
    return isCreverseAcademy(this._academy?.academyGroupType)
  }

  get studentId() {
    return this._student?.id
  }

  get student() {
    this._student?.schoolType
    return this._student
  }

  get studentSchoolTypeWithGrade() {
    const student = this._student
    if (student?.schoolType) {
      return SCHOOL_TYPE_KO_ABBR[student.schoolType] + student.grade
    }
    return ''
  }

  get studentAppSetting() {
    return this._student?.appSetting
  }

  get curriculumCore() {
    return this._curriculumCore
  }
  get studentDefaultCurriculumKey() {
    if (!this.student || !this.student.grade) return null
    const schoolType = this.student.schoolType
    // remote storage에 저장된 key가 없고, 학생이 고등학생이라면 `고등수학(상)`의 key를 사용한다.
    if (schoolType === 'HIGH') {
      return DEFAULT_HIGH_SCHOOL_CURRICULUM_KEY
    }

    if (schoolType === 'ELEMENTARY' || schoolType === 'MIDDLE') {
      const curriculum = [
        ...(this.curriculumCore?.getGradeSemesterElementary({ includeAll: false }) ?? []),
        ...(this.curriculumCore?.getGradeSemesterMiddle({ includeAll: false }) ?? []),
      ].find(({ revision, gradeSubject, schoolType: _schoolType }) => {
        if (!this.student?.grade) return false
        return (
          revision !== 'CURRICULUM_22' &&
          _schoolType === schoolType &&
          gradeSubject === String(this.student.grade)
        )
      })
      return curriculum?.trieKey ?? null
    }
  }

  get studentCurriculumInfo() {
    if (!this.studentDefaultCurriculumKey) return null

    const curriculumInfo = this.curriculumCore?.getItemByTrieKey(this.studentDefaultCurriculumKey)

    return curriculumInfo
  }

  get isSchoolflat() {
    return (
      envUtils.isSchool ||
      !!(this._academy?.customerType && this._academy.customerType?.includes('B2G'))
    )
  }

  get isElementarySchoolflat() {
    return this.student?.schoolType === 'ELEMENTARY' && this.isSchoolflat
  }

  get host() {
    if (this.isSchoolflat) {
      return 'schoolflat.com'
    }
    return domainProvider.TARGET_DOMAIN
  }

  get appKorName() {
    if (this.isSchoolflat) {
      return '스쿨플랫'
    }
    return '매쓰플랫'
  }

  /**
   * 크레버스는 브랜드명을 노출하지 않으므로 문구 앞의 브랜드명을 통째로 비운다.
   * 조사가 붙는 자리("매쓰플랫이 분석")는 josa 를 넘겨 조사까지 함께 사라지게 한다.
   */
  appKorNamePrefix(josa = '') {
    return this.isCreverse ? '' : `${this.appKorName}${josa} `
  }

  get agencyNameInKor() {
    if (this.isSchoolflat) {
      return '학교'
    }
    return '학원'
  }

  get Ai튜터_사용가능_여부() {
    return (
      this.studentAppSetting?.개념힌트_사용 ||
      this.studentAppSetting?.풀이확인_사용 ||
      this.studentAppSetting?.풀이분석_사용
    )
  }

  get signUpFrom() {
    return this._academy?.signUpFrom || null
  }

  async loadUser() {
    const storedUserId = localStorageService.user.studentId
    if (storedUserId) {
      const fetchedStudentInfo = await studentApi.getStudent(storedUserId)
      const appSetting = await studentAppSettingLoader.getStudentAppSettings(storedUserId)

      runInAction(() => {
        this._student = {
          ...fetchedStudentInfo,
          appSetting,
        }
      })
    }
  }
}

export const commonRepo = new CommonRepo()

if (isDevelopMode) globalThis._commonRepo = commonRepo
